"use client";

import { createContext, useContext, useState } from 'react';
import type { VariantSelection } from '@/lib/variants';

interface CartItem { barcode: string; requestQty: number; isWaitlisted?: boolean; [key: string]: unknown; }
interface CartContextValue {
  cartItems: CartItem[];
  addToCart: (item: Record<string, unknown>, qty?: number, options?: { waitlisted?: boolean }) => void;
  addKitToCart: (parentItem: any, getKitItems: (kitId: string) => any[]) => number;
  // itemName is optional but should be passed whenever the barcode might be
  // null (barcode-less Supply items) — otherwise this can't tell two
  // different null-barcode entries apart and would affect both at once.
  // homeGroup is likewise needed whenever the barcode-less item is an
  // attachment (e.g. "Antenna" bundled under a specific Walkie case) —
  // itemName alone collides across every other parent's identically-named
  // attachment; homeGroup says which specific parent this row belongs to.
  removeFromCart: (barcode: string | null, itemName?: string, homeGroup?: string | null) => void;
  updateQty: (barcode: string | null, qty: number, itemName?: string, homeGroup?: string | null) => void;
  // [GAlpha#3] Edits a Supply variant item's breakdown (e.g. 2 Black + 3
  // Yellow Gaff Tape) after it's already in the cart — "the dropdown buried
  // in [the cart line]" per the user's own description. null = no
  // preference; partial allocation (selected < requestQty) is valid, the
  // remainder is implicitly "no preference" too.
  setVariantSelection: (barcode: string | null, selection: VariantSelection | null, itemName?: string, homeGroup?: string | null) => void;
  // A Group's cart line stays one line by default (its own barcode) — this
  // toggles a specific member out of (or back into) that line, so a
  // student can drop e.g. one lens out of a 5-lens case without splitting
  // the whole cart into N separate lines. Tracked as `excludedMembers` on
  // the Group's own cart item; submission expands the Group into its
  // non-excluded members' own barcodes only when this list is non-empty.
  toggleGroupMemberExclusion: (groupBarcode: string, memberIdentifier: string, excluded: boolean) => void;
  clearCart: () => void;
}

const STORAGE_KEY = 'valencia-cart';
function readStorage(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
}
function writeStorage(items: CartItem[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItem[]>(readStorage);

  // Quantity-tracked Supply items can legitimately have a null barcode
  // (036_barcode_optional_for_supply.sql) — comparing `i.barcode ===
  // item.barcode` alone treats every distinct null-barcode item as "the
  // same item already in cart" the moment a second one is added (null ===
  // null), silently dropping it. Fall back to itemName to tell them apart
  // when barcode is null; real barcodes are unique enough on their own.
  //
  // itemName alone isn't quite enough either: a barcode-less item attached
  // to a home_group parent (e.g. "Antenna"/"Walkie Batteries" bundled with
  // a Walkie case) shares its generic name with every OTHER case's
  // identically-named attachment — confirmed live: requesting Walkies
  // across two different cases silently dropped the second case's antennas
  // and batteries because they read as "already in cart" the moment the
  // first case's same-named attachment landed. `homeGroup` (which specific
  // parent this row is actually attached to) disambiguates that without
  // affecting genuine Supply variants, which have no home_group at all
  // (both sides null, so they still collapse exactly as before).
  function isSameCartItem(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
    if (a.barcode != null || b.barcode != null) return a.barcode === b.barcode;
    return a.itemName === b.itemName && (a.homeGroup ?? null) === (b.homeGroup ?? null);
  }

  function addToCart(item: Record<string, unknown>, qty = 1, options?: { waitlisted?: boolean }) {
    setCartItems(prev => {
      if (prev.find(i => isSameCartItem(i, item))) return prev;
      const next = [...prev, { ...item, requestQty: Math.max(1, qty), isWaitlisted: options?.waitlisted ?? false } as CartItem];
      writeStorage(next);
      return next;
    });
  }

  function removeFromCart(barcode: string | null, itemName?: string, homeGroup?: string | null) {
    setCartItems(prev => {
      const next = barcode != null
        ? prev.filter(i => i.barcode !== barcode)
        : prev.filter(i => !(i.barcode == null && i.itemName === itemName && (i.homeGroup ?? null) === (homeGroup ?? null)));
      writeStorage(next);
      return next;
    });
  }

  function updateQty(barcode: string | null, qty: number, itemName?: string, homeGroup?: string | null) {
    setCartItems(prev => {
      const matches = (i: CartItem) => barcode != null ? i.barcode === barcode : (i.barcode == null && i.itemName === itemName && (i.homeGroup ?? null) === (homeGroup ?? null));
      const next = prev.map(i => matches(i) ? { ...i, requestQty: qty } : i);
      writeStorage(next);
      return next;
    });
  }

  function setVariantSelection(barcode: string | null, selection: VariantSelection | null, itemName?: string, homeGroup?: string | null) {
    setCartItems(prev => {
      const matches = (i: CartItem) => barcode != null ? i.barcode === barcode : (i.barcode == null && i.itemName === itemName && (i.homeGroup ?? null) === (homeGroup ?? null));
      const next = prev.map(i => matches(i) ? { ...i, variantSelection: selection } : i);
      writeStorage(next);
      return next;
    });
  }

  function toggleGroupMemberExclusion(groupBarcode: string, memberIdentifier: string, excluded: boolean) {
    setCartItems(prev => {
      const next = prev.map(i => {
        if (i.barcode !== groupBarcode) return i;
        const current = Array.isArray(i.excludedMembers) ? (i.excludedMembers as string[]) : [];
        const withoutIt = current.filter(m => m !== memberIdentifier);
        const updated = excluded ? [...withoutIt, memberIdentifier] : withoutIt;
        return { ...i, excludedMembers: updated };
      });
      writeStorage(next);
      return next;
    });
  }

  function addKitToCart(parentItem: any, getKitItems: (kitId: string) => any[]): number {
    const members = getKitItems(parentItem.kitId);
    members.forEach(member => addToCart(member, member.qty || 1));
    return members.length;
  }

  function clearCart() {
    setCartItems([]);
    if (typeof window !== 'undefined') localStorage.removeItem(STORAGE_KEY);
  }

  return (
    <CartContext.Provider value={{ cartItems, addToCart, addKitToCart, removeFromCart, updateQty, setVariantSelection, toggleGroupMemberExclusion, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
